import { ref } from 'vue';
import { defineStore } from 'pinia';
import { escape } from 'lodash';
import jobBoardApi from '../api/job-board.js';
import { setAuthState } from '../composables/auth';
import { alertFrequency, alertName } from '../utils/job-alerts.js';
import { useNotifications } from './notifications.store.js';

const { isAuthenticated } = setAuthState();

/** How long "{name} alert deleted." offers Undo before the alert is deleted for good (EN-1304). */
export const DELETE_UNDO_MS = 7000;

/**
 * The `meta` keys that decide whether a search describes an alert the user already has.
 *
 * An allowlist rather than "every key that happens to be in the payload", so a new payload
 * key cannot silently join the identity set. Deliberately absent:
 *
 * - `criteria` — its values already mirror these flat filters, and its rank order is a
 *   ranking preference, not a different alert, so re-ranking must not spawn a duplicate.
 * - `email_frequency` — editable per alert, so it never distinguishes two alerts.
 * - `page`, `jobs_per_page`, `sort_by`, `sort_direction` — view state. Comparing them turned
 *   the alert toggle off on lazy-load and on sort, because both re-run the existence check.
 * - `longitude`, `latitude` — geocoded from `location`, so comparing floats adds nothing
 *   over `location` plus `radius`.
 */
export const ALERT_IDENTITY_META_KEYS = [
	'work_setting',
	'industry',
	'last_posted',
	'radius',
	'min_experience',
	'max_experience',
	'min_salary',
	'max_salary',
	'auto_apply_filter',
];

/**
 * Normalise one identity value into a string comparable with `===`.
 *
 * Treats a missing key, `null`, `''` and `[]` alike as "not set" so an alert saved before a
 * filter existed still matches a search where that filter is off; ignores array order, since
 * picking the same industries in a different order is the same filter; and reads a scalar as
 * a single-element array so a v1-era `'tech'` still matches v3's `['tech']`.
 *
 * Identity keys only ever hold primitives or arrays of primitives.
 */
const toComparable = (value) => {
	const values = (Array.isArray(value) ? value : [value])
		.filter((item) => item !== null && item !== undefined && item !== '')
		.map((item) => String(item))
		.sort();

	return JSON.stringify(values);
};

/**
 * The job title, compared without case: titles used to be saved lowercased and are now saved as
 * typed (EN-1304), so "UX Designer" has to find the "ux designer" alert it already has.
 */
const toComparableRole = (role) => toComparable(typeof role === 'string' ? role.trim().toLowerCase() : role);

/**
 * Whether a saved alert describes the same search as the candidate built from the current
 * filters. Compares by value: the payload carries arrays (v3 sends them for `industry` and
 * `work_setting`) and `===` on those compares by reference, so every alert missed and
 * re-toggling created a duplicate subscription.
 */
export const isSameJobAlert = (savedAlert, candidate) =>
	toComparableRole(savedAlert?.role) === toComparableRole(candidate?.role)
	&& toComparable(savedAlert?.location) === toComparable(candidate?.location)
	&& ALERT_IDENTITY_META_KEYS.every(
		(key) => toComparable(savedAlert?.meta?.[key]) === toComparable(candidate?.meta?.[key])
	);

export const useJobAlertsStore = defineStore('jobAlerts', () => {
	const notifications = useNotifications();

	// State
	const isEditModalVisible = ref(false);
	const jobAlertExist = ref(false);
	const loadingJobAlerts = ref(false);
	const switchPosition = ref(false);
	const jobAlert = ref(null);
	const jobAlerts = ref([]);
	const jobBoardUserSettings = ref(null);

	// Alerts deleted from the list whose Undo toast is still up, by id: `{ alert, predecessorId, toastId }`.
	// The DELETE is sent when the toast closes, so Undo only has to put the row back (EN-1304).
	//
	// `predecessorId` (the id of whatever alert preceded this one in the list at delete time, or null if it
	// was first) is used instead of a plain array index so that undoing several pending deletions in any
	// order still restores the original order: a numeric index recorded against a list that keeps shrinking
	// and growing as other deletions/undos happen goes stale as soon as a second delete is in flight.
	const pendingDeletions = new Map();

	// Actions
	const fetchJobAlerts = async() => {
		if (!isAuthenticated()) {return;}

		loadingJobAlerts.value = true;
		try {
			const alerts = await jobBoardApi.getFilters();

			// A refetch while an Undo toast is up must not bring the deleted alert back.
			jobAlerts.value = alerts.filter((alert) => !pendingDeletions.has(alert.id));
		} finally {
			loadingJobAlerts.value = false;
		}
	};

	const checkIfJobAlertExist = async (jobSearchFilters) => {
		switchPosition.value = false;
		const { job_title, location, ...rest } = jobSearchFilters;
		const filterData = {
			// Saved as typed, so the alert's name keeps the user's capitals ("UX Designer").
			role: job_title?.trim() || 'Title not set',
			location: location || 'Not set',
			meta: {
				email_frequency: 'weekly',  // Default value
				...rest
			}
		};

		await fetchJobAlerts();

		// Check if a job alert with the same filters already exists
		const filterExist = jobAlerts.value.find((item) => isSameJobAlert(item, filterData));
		if (filterExist) {
			jobAlert.value = filterExist;
			switchPosition.value = filterExist.active;
			jobAlertExist.value = true;
		} else {
			jobAlert.value = filterData;
			jobAlertExist.value = false;
		}
	};

	const saveCurrentJobAlert = async() => {
		switchPosition.value = true;
		// The list comes back newest first, so the alert just created is found by its id, not its place.
		const created = await jobBoardApi.saveFilters(jobAlert.value);
		await fetchJobAlerts();

		jobAlertExist.value = true;
		jobAlert.value = jobAlerts.value.find((item) => item.id === created?.id) ?? created;
	};

	const updateJobAlertById = async (jobAlertId, data) => {
		await jobBoardApi.updateFilter(jobAlertId, data);
		const findJobAlertIndexById = jobAlerts.value.findIndex((item) => item.id === jobAlertId);
		const updateJobAlertIndex = { ...jobAlerts.value[findJobAlertIndexById], ...data };
		jobAlerts.value.splice(findJobAlertIndexById, 1, updateJobAlertIndex);

		jobAlert.value = jobAlerts.value.find((item) => item.id === jobAlertId);
		switchPosition.value = jobAlert.value.active;
		isEditModalVisible.value = false;
	};

	/** Change one alert in the list where it stands, and return the alert as it was. */
	const replaceJobAlert = (id, changes) => {
		const index = jobAlerts.value.findIndex((item) => item.id === id);

		if (index === -1) {
			return null;
		}

		const previous = jobAlerts.value[index];
		jobAlerts.value.splice(index, 1, { ...previous, ...changes });

		// The search page's alert toggle shows the same alert.
		if (jobAlert.value?.id === id) {
			jobAlert.value = jobAlerts.value[index];
			switchPosition.value = jobAlert.value.active;
		}

		return previous;
	};

	/**
	 * Save the Edit Job Alert form (EN-1305). The row changes once the server has the change; a failure is thrown
	 * back to the form, which keeps the user's edits on screen and says it couldn't save.
	 */
	const saveJobAlert = async (alert, changes) => {
		await jobBoardApi.updateFilter(alert.id, changes);

		replaceJobAlert(alert.id, changes);
		notifications.notify(`Changes saved for ${escape(alertName({ ...alert, ...changes }))}.`);
	};

	/** Turn an alert on or pause it. The row changes at once, and changes back if the save fails. */
	const setJobAlertActive = async (alert, active) => {
		const name = escape(alertName(alert));
		const previous = replaceJobAlert(alert.id, { active });

		try {
			await jobBoardApi.updateFilter(alert.id, { active });

			notifications.notify(active
				? `${name} alert is on. You'll get new jobs ${alertFrequency(alert)}.`
				: `${name} alert is paused. We won't email you until you turn it back on.`);
		} catch {
			if (previous) {
				replaceJobAlert(alert.id, { active: previous.active });
			}

			notifications.notify(
				`Couldn't ${active ? 'turn on' : 'pause'} the ${name} alert. Please try again.`,
				'error',
			);
		}
	};

	/** Change how often an alert is emailed. The row changes at once, and changes back if the save fails. */
	const setJobAlertFrequency = async (alert, frequency) => {
		const name = escape(alertName(alert));
		const meta = { ...alert.meta, email_frequency: frequency };
		const previous = replaceJobAlert(alert.id, { meta });

		try {
			await jobBoardApi.updateFilter(alert.id, { meta });

			notifications.notify(`You'll get new ${name} jobs ${frequency}.`);
		} catch {
			if (previous) {
				replaceJobAlert(alert.id, { meta: previous.meta });
			}

			notifications.notify(`Couldn't change how often you get the ${name} alert. Please try again.`, 'error');
		}
	};

	// The search page's alert toggle follows the alert it shows out of the list and back.
	const syncSearchToggle = (alert, exists) => {
		if (jobAlert.value?.id !== alert.id) {
			return;
		}

		jobAlertExist.value = exists;
		switchPosition.value = exists && alert.active;
	};

	const putBackJobAlert = (alert, predecessorId) => {
		// Not found covers both "was first" (predecessorId is null) and "still mid-Undo itself" — either
		// way this alert belongs at the front until its predecessor is back in the list.
		const predecessorIndex = jobAlerts.value.findIndex((item) => item.id === predecessorId);
		jobAlerts.value.splice(predecessorIndex + 1, 0, alert);
		syncSearchToggle(alert, true);
	};

	// A tab closed while an Undo toast is up would otherwise keep the alert the user deleted.
	const deletePendingOnUnload = () => {
		pendingDeletions.forEach(({ toastId }, id) => {
			pendingDeletions.delete(id);
			jobBoardApi.deleteFilterOnUnload(id);
			// Its Undo would only bring back a row the server no longer has, should the page be restored.
			notifications.remove(toastId);
		});

		window.removeEventListener('pagehide', deletePendingOnUnload);
	};

	const takePendingDeletion = (id) => {
		const pending = pendingDeletions.get(id);

		pendingDeletions.delete(id);

		if (pendingDeletions.size === 0) {
			window.removeEventListener('pagehide', deletePendingOnUnload);
		}

		return pending;
	};

	const undoDeleteJobAlert = (id) => {
		const pending = takePendingDeletion(id);

		if (pending) {
			putBackJobAlert(pending.alert, pending.predecessorId);
		}
	};

	const finishDeleteJobAlert = async (id) => {
		const pending = takePendingDeletion(id);

		if (!pending) {
			return;
		}

		try {
			await jobBoardApi.deleteFilter(id);
		} catch {
			putBackJobAlert(pending.alert, pending.predecessorId);
			notifications.notify(`Couldn't delete the ${escape(alertName(pending.alert))} alert. Please try again.`, 'error');
		}
	};

	/**
	 * Take an alert off the list at once and offer Undo (EN-1304). Nothing is sent until the toast closes: Undo
	 * then only has to put the row back, so the alert keeps its settings and its send history, and no first email
	 * goes out as it would for a re-created alert.
	 */
	const deleteJobAlert = (alert) => {
		const index = jobAlerts.value.findIndex((item) => item.id === alert.id);

		if (index === -1) {
			return;
		}

		const predecessorId = jobAlerts.value[index - 1]?.id ?? null;

		jobAlerts.value.splice(index, 1);
		syncSearchToggle(alert, false);

		if (pendingDeletions.size === 0) {
			window.addEventListener('pagehide', deletePendingOnUnload);
		}

		pendingDeletions.set(alert.id, { alert, predecessorId, toastId: null });

		const toastId = notifications.notify(
			`${escape(alertName(alert))} alert deleted.`,
			'success',
			DELETE_UNDO_MS,
			null,
			'Undo',
			() => undoDeleteJobAlert(alert.id),
			{ onClose: () => finishDeleteJobAlert(alert.id) },
		);

		pendingDeletions.get(alert.id).toastId = toastId;
	};

	const fetchJobUserSettings = async () => {
		jobBoardUserSettings.value = await jobBoardApi.getPersonalizedJobRecommendations();
	};

	const getJobBoardUserSettingsByKey = (key) => {
		const element = jobBoardUserSettings.value.find((el) => el.key === key);
		return element.value;
	};

	const updateUserSettings = async(key, value) => {
		await jobBoardApi.updatePersonalizedJobRecommendations({ key, value });
	};

	const resetState = () => {
		isEditModalVisible.value = false;
		jobAlertExist.value = false;
		loadingJobAlerts.value = false;
		switchPosition.value = false;
		jobAlert.value = null;
		jobAlerts.value = [];
		jobBoardUserSettings.value = null;
		pendingDeletions.clear();
		window.removeEventListener('pagehide', deletePendingOnUnload);
	};

	return {
		jobAlerts,
		switchPosition,
		jobAlert,
		loadingJobAlerts,
		isEditModalVisible,
		jobAlertExist,
		jobBoardUserSettings,

		resetState,
		fetchJobAlerts,
		updateJobAlertById,
		setJobAlertActive,
		setJobAlertFrequency,
		deleteJobAlert,
		saveJobAlert,
		updateUserSettings,
		saveCurrentJobAlert,
		checkIfJobAlertExist,
		fetchJobUserSettings,
		getJobBoardUserSettingsByKey
	};
});
